import LandingLayout from './LandingLayout';
import {
  FileText,
  Receipt,
  Award,
  Users,
  ShoppingCart,
  Building2,
  TrendingUp,
  Shield,
  CreditCard,
  Mail,
  BarChart3,
  Calendar,
  FileCheck,
  Settings,
  Globe,
} from 'lucide-react';

export default function Features() {
  const mainFeatures = [
    {
      icon: FileText,
      title: 'Invoice Management',
      description: 'Create professional invoices with materials and labour sections. Track payment status, send reminders, and generate PDFs.',
      details: [
        'Draft, send, and track invoice status',
        'Separate materials and labour sections',
        'Automatic VAT calculations',
        'PDF generation and email delivery',
        'Payment tracking and overdue alerts',
      ],
    },
    {
      icon: Receipt,
      title: 'Estimates & Quotes',
      description: 'Generate detailed estimates that can be converted to invoices instantly. Track expiry dates and client acceptance.',
      details: [
        'Professional estimate templates',
        'One-click conversion to invoice',
        'Expiry date tracking',
        'Client acceptance workflow',
        'PDF export and sharing',
      ],
    },
    {
      icon: Award,
      title: 'BS7671 Certification',
      description: 'Manage electrical certification including EIC, EICR, and MEWC. Generate compliant certificates for your clients.',
      details: [
        'EIC (Electrical Installation Certificate)',
        'EICR (Electrical Installation Condition Report)',
        'MEWC (Minor Electrical Works Certificate)',
        'Compliant with UK regulations',
        'Client portal access to certificates',
      ],
    },
    {
      icon: Users,
      title: 'Client Management',
      description: 'Complete CRM system for managing your customers. Store contact details, addresses, payment history, and notes.',
      details: [
        'Individual and company client types',
        'Complete contact information',
        'Payment history tracking',
        'Credit limit management',
        'Quick client creation from invoices',
      ],
    },
    {
      icon: ShoppingCart,
      title: 'Purchase Tracking',
      description: 'Record supplier purchases with line items. Track materials for accurate job costing and inventory management.',
      details: [
        'Detailed purchase line items',
        'Supplier reference tracking',
        'Materials lookup for invoices',
        'Purchase payment tracking',
        'Multi-account banking integration',
      ],
    },
    {
      icon: Building2,
      title: 'Supplier Management',
      description: 'Manage supplier information, credit terms, and purchase history. Track balances and payment schedules.',
      details: [
        'Supplier contact details',
        'Credit terms management',
        'Balance tracking',
        'Purchase history',
        'Payment scheduling',
      ],
    },
    {
      icon: TrendingUp,
      title: 'Financial Reports',
      description: 'Comprehensive financial reporting including Profit & Loss, Balance Sheet, VAT Summary, and VAT Returns.',
      details: [
        'Profit & Loss statements',
        'Balance Sheet reports',
        'VAT Summary and Returns',
        'Date range filtering',
        'UK tax year presets',
      ],
    },
    {
      icon: Shield,
      title: 'Banking & Accounts',
      description: 'Multi-account banking with transaction tracking. Automatic account creation for Petty Cash and Drawings.',
      details: [
        'Multiple bank accounts',
        'Transaction tracking',
        'Opening balance management',
        'Auto-created system accounts',
        'Full transaction history',
      ],
    },
  ];

  const additionalFeatures = [
    {
      icon: CreditCard,
      title: 'Stripe Payment Integration',
      description: 'Accept online payments directly through invoices. Secure payment processing with automatic reconciliation.',
    },
    {
      icon: Mail,
      title: 'Email Notifications',
      description: 'Send invoices and estimates via email with secure signed links. No attachments required.',
    },
    {
      icon: Globe,
      title: 'Client Portal',
      description: 'Give clients access to view invoices, estimates, and certificates. Enable online payments and messaging.',
    },
    {
      icon: BarChart3,
      title: 'Dashboard Analytics',
      description: 'Real-time financial overview with 12-month revenue charts and activity feeds.',
    },
    {
      icon: Calendar,
      title: 'Date Management',
      description: 'Track creation dates, issue dates, due dates, and expiry dates for all documents.',
    },
    {
      icon: FileCheck,
      title: 'Document Management',
      description: 'Archive and soft-delete functionality. Maintain complete audit trails.',
    },
    {
      icon: Settings,
      title: 'Custom Branding',
      description: 'Customize your invoices and estimates with your logo, colors, and company details.',
    },
  ];

  return (
    <LandingLayout>
      {/* Hero */}
      <section className="bg-gradient-to-br from-orange-50 via-white to-orange-50 dark:from-gray-900 dark:via-gray-900 dark:to-gray-800 py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl sm:text-5xl font-bold text-gray-900 dark:text-white mb-6">
            Powerful Features for
            <span className="block text-orange-600 dark:text-orange-500">UK Tradespeople</span>
          </h1>
          <p className="text-xl text-gray-600 dark:text-gray-300 max-w-3xl mx-auto">
            Everything you need to manage your trade business efficiently and professionally.
          </p>
        </div>
      </section>

      {/* Main Features */}
      <section className="py-20 bg-white dark:bg-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="space-y-24">
            {mainFeatures.map((feature, index) => {
              const Icon = feature.icon;
              return (
                <div
                  key={index}
                  className={`flex flex-col ${
                    index % 2 === 0 ? 'lg:flex-row' : 'lg:flex-row-reverse'
                  } gap-12 items-center`}
                >
                  <div className="flex-1">
                    <div className="inline-flex items-center justify-center h-16 w-16 bg-orange-100 dark:bg-orange-900/30 rounded-xl mb-6">
                      <Icon className="h-8 w-8 text-orange-600 dark:text-orange-500" />
                    </div>
                    <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-4">
                      {feature.title}
                    </h2>
                    <p className="text-lg text-gray-600 dark:text-gray-400 mb-6">
                      {feature.description}
                    </p>
                    <ul className="space-y-3">
                      {feature.details.map((detail, idx) => (
                        <li key={idx} className="flex items-start">
                          <span className="text-orange-600 dark:text-orange-500 mr-3 mt-1">✓</span>
                          <span className="text-gray-700 dark:text-gray-300">{detail}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div className="flex-1">
                    <div className="bg-gradient-to-br from-orange-100 to-orange-50 dark:from-gray-800 dark:to-gray-700 rounded-2xl p-8 h-64 flex items-center justify-center">
                      <Icon className="h-32 w-32 text-orange-200 dark:text-orange-900/50" />
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Additional Features Grid */}
      <section className="py-20 bg-gray-50 dark:bg-gray-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 dark:text-white mb-4">
              And So Much More
            </h2>
            <p className="text-xl text-gray-600 dark:text-gray-400">
              Additional features to help you run your business smoothly
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {additionalFeatures.map((feature, index) => {
              const Icon = feature.icon;
              return (
                <div
                  key={index}
                  className="p-6 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 hover:border-orange-500 dark:hover:border-orange-500 transition-colors"
                >
                  <div className="h-12 w-12 bg-orange-100 dark:bg-orange-900/30 rounded-lg flex items-center justify-center mb-4">
                    <Icon className="h-6 w-6 text-orange-600 dark:text-orange-500" />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                    {feature.title}
                  </h3>
                  <p className="text-gray-600 dark:text-gray-400">
                    {feature.description}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>
    </LandingLayout>
  );
}


